import { FC, useEffect, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import cx from 'classnames';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { BottomPanelKey, EventSchema, StreamingPlatform as StreamingPlatformType, UserAction } from 'types';
import { EVENT_TITLE_MAX_LENGTH, getPlatformNameByKey, mapStreamingPlatformToUserAction } from 'utils';
import {
  activeStreamingPlatformsSelector,
  authSelector,
  hasMembershipPriceSelector,
  isMembersOnlyAvailableSelector,
  isRecordingAvailableSelector,
  linkedStreamingPlatformsSelector,
  notLinkedStreamingPlatformsSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService, RTCService, tracker } from 'services';
import { useBottomSheet, useRouter, useStore, useStreamingAccounts, useToast } from 'hooks';
import { AddStreamingPlatformsPanel, Backstage, BottomPanels, StreamingPlatform } from 'containers';
import { Input, Loader, Switch } from 'components';
import { CameraOnNewIcon, MicrophoneOnNewIcon, PlusIcon, RecordingIcon, UserGroupIcon } from 'components/Icon';

import styles from './SetupPanel.module.scss';

export type SetupPanelProps = {
  type?: 'new' | 'scheduled';
  channelName?: string;
  eventCode?: string;
  eventName?: string;
  isMembersOnly?: boolean;
  isRecordingEnabled?: boolean;
  onSubmitted?(
    channelName: string,
    eventCode: string,
    options: {
      audioMuted: boolean;
      videoMuted: boolean;
    }
  ): void;
};

type EventDetails = { name: string; isRecordingEnabled: boolean; isMembersOnly: boolean };

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Event topic is required')
    .max(EVENT_TITLE_MAX_LENGTH, `must be less than ${EVENT_TITLE_MAX_LENGTH} characters`),
  isMembersOnly: yup.boolean().required(),
  isRecordingEnabled: yup.boolean().required()
});

const SetupPanel: FC<SetupPanelProps> = ({
  type = 'new',
  eventCode,
  eventName = '',
  isMembersOnly,
  isRecordingEnabled,
  onSubmitted
}): JSX.Element => {
  const toast = useToast();
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    auth,
    activeStreamingPlatforms,
    isMembersOnlyAvailable,
    isRecordingAvailable,
    hasMembershipPrice,
    linkedPlatforms,
    notLinkedPlatforms
  } = useStore(state => ({
    auth: authSelector(state),
    activeStreamingPlatforms: activeStreamingPlatformsSelector(state),
    isMembersOnlyAvailable: isMembersOnlyAvailableSelector(state),
    isRecordingAvailable: isRecordingAvailableSelector(state),
    hasMembershipPrice: hasMembershipPriceSelector(state),
    linkedPlatforms: linkedStreamingPlatformsSelector(state),
    notLinkedPlatforms: notLinkedStreamingPlatformsSelector(state)
  }));
  const isMembersOnlyToggleActive = isMembersOnlyAvailable && hasMembershipPrice;
  const isRecordingToggleActive = isRecordingAvailable && hasMembershipPrice;

  const closePreviewTracksOnClose = useRef(true);
  const micOnly = useRef(false);
  const isNewEvent = type === 'new';

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: eventName || '',
      isRecordingEnabled: isRecordingToggleActive && (isNewEvent || !!isRecordingEnabled),
      isMembersOnly: !!isMembersOnly
    }
  });
  const bottomSheet = useBottomSheet<BottomPanelKey.ADD_STREAMING_ACCOUNT>();
  const streamingAccounts = useStreamingAccounts({
    onLinked: () => bottomSheet.close()
  });

  // reset form default values when initial values change
  useEffect(() => {
    form.reset({
      name: eventName || '',
      isRecordingEnabled: isRecordingToggleActive && (isNewEvent || !!isRecordingEnabled),
      isMembersOnly: !!isMembersOnly
    });
  }, [eventName, isNewEvent, isRecordingEnabled, isMembersOnly]);

  useEffect(
    () => () => {
      if (closePreviewTracksOnClose.current) {
        RTCService.closeLocalTracks();
      }
    },
    []
  );

  const openAccountsPanel = () => bottomSheet.open(BottomPanelKey.ADD_STREAMING_ACCOUNT);

  const closeAccountsPanel = () => bottomSheet.close();

  const toggleAudio = (muted: boolean) => tracker.toggleMicrophoneOnSetupPanel(!muted);

  const toggleVideo = (muted: boolean) => tracker.toggleCameraOnSetupPanel(!muted);

  const toggleAccount = (platform: StreamingPlatformType, checked: boolean) => {
    if (checked) streamingAccounts.enable(platform);
    else streamingAccounts.disable(platform);
  };

  const selectAccount = (platform: StreamingPlatformType) => streamingAccounts.link(platform);

  const startNew = async (values: EventDetails): Promise<EventSchema> => {
    try {
      tracker.startEvent();

      const { name: hostName, image: hostAvatar } = auth;
      const { data } = await api.requests.ltv.createEvent({
        eventName: values.name,
        membersOnly: values.isMembersOnly,
        enableRecording: values.isRecordingEnabled,
        hostName,
        hostAvatar
      });

      tracker.startEventSuccess(data.code, values.isMembersOnly);

      return data;
    } catch (e) {
      LoggerService.log('START_A_NEW_EVENT', e);

      if (e instanceof Error) {
        tracker.startEventFail(e);
      }

      throw e;
    }
  };

  const startScheduled = async (): Promise<EventSchema> => {
    try {
      tracker.startScheduledEvent(eventCode as string);

      const { data } = await api.requests.ltv.startEvent(eventCode as string);

      tracker.startScheduledEventSuccess(data.code);

      return data;
    } catch (e) {
      LoggerService.log('START_A_SCHEDULED_EVENT', e);

      if (e instanceof Error) {
        tracker.startScheduledEventFail(eventCode as string, e);
      }

      throw e;
    }
  };

  const prepareStreamingPlatforms = () => {
    const streamingActionTriggers = activeStreamingPlatforms
      .map(platform => platform.key)
      .map(mapStreamingPlatformToUserAction)
      .filter(Boolean) as UserAction[];

    dispatch(callbackTriggersActions.set(streamingActionTriggers));
  };

  const handleOnStartWithMicOnly = () => {
    micOnly.current = true;
  };

  const handleOnStartWithMicAndCamera = () => {
    micOnly.current = false;
  };

  const onSubmit = async (values: EventDetails) => {
    closePreviewTracksOnClose.current = false;

    let event!: EventSchema;

    try {
      switch (type) {
        case 'new': {
          event = await startNew({
            name: values.name,
            isRecordingEnabled: values.isRecordingEnabled,
            isMembersOnly: values.isMembersOnly
          });
          break;
        }

        case 'scheduled': {
          event = await startScheduled();
          break;
        }
      }

      prepareStreamingPlatforms();

      if (micOnly.current && RTCService.cameraTrack) {
        await RTCService.closeLocalTrack(RTCService.cameraTrack);
        RTCService.cameraTrack = null;
      }

      onSubmitted?.(event.channelName, event.code, {
        audioMuted: false,
        videoMuted: micOnly.current
      });
    } catch (error) {
      LoggerService.log('START_EVENT', error);

      if (error instanceof Error) {
        toast.error(error.message, { id: 'START_EVENT' });
      }
    }
  };

  const onEnablePremiumForMembersOnly = () => {
    if (!isMembersOnlyAvailable) {
      router.push('/pricing', { membersOnly: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  const onEnablePremiumForRecording = () => {
    if (!isRecordingAvailable) {
      router.push('/pricing', { recording: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  return (
    <>
      <div className={styles.container}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {form.formState.isSubmitting && <Loader />}

            <Controller
              name="name"
              control={form.control}
              render={({ field }) => (
                <Input
                  className={styles.topic}
                  invalid={!!form.formState.errors.name}
                  placeholder="Add a Topic"
                  maxLength={EVENT_TITLE_MAX_LENGTH}
                  disabled={!!eventName}
                  feedback={form.formState.errors.name?.message}
                  {...field}
                />
              )}
            />

            <ul className={styles.toggles}>
              <li>
                <Controller
                  name="isMembersOnly"
                  control={form.control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      className={cx(styles.toggle, styles.members)}
                      icon={<UserGroupIcon />}
                      title="Members Only"
                      checked={value}
                      badge={!isMembersOnlyAvailable ? 'Premium' : ''}
                      disabled={!isMembersOnlyToggleActive || !isNewEvent}
                      onChange={() => onChange?.(!value)}
                      onClick={onEnablePremiumForMembersOnly}
                    />
                  )}
                />
              </li>
              <li>
                <Controller
                  name="isRecordingEnabled"
                  control={form.control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      className={cx(styles.toggle, styles.recording)}
                      icon={<RecordingIcon />}
                      title="Recording"
                      checked={value}
                      badge={!isRecordingAvailable ? 'Premium' : ''}
                      disabled={!isRecordingToggleActive || !isNewEvent}
                      onChange={() => onChange?.(!value)}
                      onClick={onEnablePremiumForRecording}
                    />
                  )}
                />
              </li>
            </ul>

            <div className={styles.preview}>
              <Backstage
                defaultAudioMuted
                defaultVideoMuted
                onAudioMutedChanged={toggleAudio}
                onVideoMutedChanged={toggleVideo}
              >
                <Backstage.Microphone />
                <Backstage.Camera />
              </Backstage>
            </div>

            <div className={styles.accounts}>
              <div className={styles.link}>
                <div className={styles.content}>
                  <p className={styles.title}>Stream live event to:</p>
                  <span className={styles.subtitle}>
                    {linkedPlatforms.length > 0
                      ? linkedPlatforms.map(linkedPlatform => getPlatformNameByKey(linkedPlatform.key))
                      : 'No linked platforms'}
                  </span>
                </div>
                {notLinkedPlatforms.length > 0 && (
                  <button type="button" className={styles.action} onClick={openAccountsPanel}>
                    <PlusIcon />
                  </button>
                )}
              </div>

              {linkedPlatforms.length > 0 && (
                <ul className={styles.list}>
                  {linkedPlatforms.map(platform => (
                    <li key={platform.key}>
                      <StreamingPlatform action="toggle" platform={platform.key} onChange={toggleAccount} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.actions}>
              <button
                type="submit"
                className={cx(styles.action, styles.micOnly)}
                disabled={form.formState.isSubmitting}
                onClick={handleOnStartWithMicOnly}
              >
                <span>Start with</span>
                <MicrophoneOnNewIcon />
              </button>
              <button
                type="submit"
                className={cx(styles.action, styles.withCam)}
                disabled={form.formState.isSubmitting}
                onClick={handleOnStartWithMicAndCamera}
              >
                <span>Start with</span>
                <MicrophoneOnNewIcon /> + <CameraOnNewIcon />
              </button>
            </div>
          </form>
        </FormProvider>
      </div>

      <BottomPanels
        inner
        panels={[
          {
            name: BottomPanelKey.ADD_STREAMING_ACCOUNT,
            show: bottomSheet.state.contentKey === BottomPanelKey.ADD_STREAMING_ACCOUNT,
            title: 'Add streaming account',
            component: (
              <div className="container text-center py-4 pb-24">
                <AddStreamingPlatformsPanel onClick={selectAccount} />
              </div>
            )
          }
        ]}
        onClose={closeAccountsPanel}
      />
    </>
  );
};

export default SetupPanel;
